<template>
  <div id="app" class="h-100" :class="[skinClasses]">
    <component :is="layout">
      <router-view />
    </component>

    <scroll-to-top v-if="enableScrollToTop" />
    <modal-change-pass :accountNew="true" :value="changPass" :hideModal="true" />
  </div>
</template>

<script>
import ScrollToTop from "@core/components/scroll-to-top/ScrollToTop.vue";

// This will be populated in `beforeCreate` hook
import { $themeColors, $themeBreakpoints, $themeConfig } from "@themeConfig";
import { provideToast } from "vue-toastification/composition";
import { watch } from "@vue/composition-api";
import useAppConfig from "@core/app-config/useAppConfig";
import ModalChangePass from "@/views/cont-public/auth/modal-change-pass.vue";
import { useWindowSize, useCssVar } from "@vueuse/core";

import store from "@/store";

const LayoutVertical = () => import("@/layouts/vertical/LayoutVertical.vue");
const LayoutFull = () => import("@/layouts/full/LayoutFull.vue");
const LayoutWr = () => import("@/layouts/wr/LayoutWr.vue");

export default {
  components: {
    // Layouts
    LayoutVertical,
    LayoutFull,
    LayoutWr,
    ModalChangePass,

    ScrollToTop,
  },
  watch: {
    "$i18n.locale": function (newVal, oldVal) {
      if (newVal == "vn") {
        document.documentElement.style.setProperty(
          "--font-family-sans-serif",
          "'Montserrat', sans-serif"
        );
        document.documentElement.style.setProperty(
          "--font-family-monospace",
          "'Montserrat', sans-serif"
        );
        // --body-font
        document.documentElement.style.setProperty(
          "--body-font",
          "'Montserrat', sans-serif"
        );
      } else {
        document.documentElement.style.setProperty(
          "--font-family-sans-serif",
          "'Poppins' ,sans-serif"
        );
        document.documentElement.style.setProperty(
          "--font-family-monospace",
          "'Poppins' ,sans-serif"
        );
        document.documentElement.style.setProperty(
          "--body-font",
          "'Poppins' ,sans-serif"
        );
      }
    },
  },
  // ! We can move this computed: layout & contentLayoutType once we get to use Vue 3
  // Currently, router.currentRoute is not reactive and doesn't trigger any change
  mounted() {
    if (this.changPass == null) {
      this.$store.commit(
        "appConfig/CHANGE_PASS",
        JSON.parse(localStorage.getItem("userData")).requestedPasswordReset == 1
          ? true
          : false
      );
    }
    if (this.$i18n.locale == "vn") {
      document.documentElement.style.setProperty(
        "--font-family-sans-serif",
        "'Montserrat', sans-serif"
      );
      document.documentElement.style.setProperty(
        "--font-family-monospace",
        "Montserrat ,sans-serif"
      );
      document.documentElement.style.setProperty(
        "--body-font",
        "'Montserrat', sans-serif"
      );
    } else {
      document.documentElement.style.setProperty(
        "--font-family-sans-serif",
        "'Poppins' ,sans-serif"
      );
      document.documentElement.style.setProperty(
        "--font-family-monospace",
        "'Poppins' ,sans-serif"
      );
      document.documentElement.style.setProperty(
        "--body-font",
        "'Poppins' ,sans-serif"
      );
    }
    this.$root.$on("bv::modal::shown", (bvEvent, modalId) => {
      document.getElementsByClassName("modal-content").forEach(function (v) {
        v.removeAttribute("tabindex");
      });
    });
    document.addEventListener(
      "click",
      function (event) {
        if (
          event.target.id === "select2--container" ||
          event.target.id === "select2-code-container" ||
          event.target.classList.value ===
            "select2-selection select2-selection--single" ||
          event.target.classList.value === "select2-selection__placeholder"
        ) {
          var searchQueryTB = document.getElementsByClassName(
            "select2-search__field"
          )[0];
          searchQueryTB && searchQueryTB.focus();
        }
      },
      true
    );
  },
  computed: {
    changPass() {
      return this.$store.state.appConfig.changePass;
    },
    layout() {
      if (this.$route.meta.layout === "wr") return "layout-wr";

      if (this.$route.meta.layout === "full") return "layout-full";
      return `layout-${this.contentLayoutType}`;
    },
    contentLayoutType() {
      return this.$store.state.appConfig.layout.type;
    },
  },
  beforeCreate() {
    // Set colors in theme
    const colors = [
      "primary",
      "secondary",
      "success",
      "info",
      "warning",
      "danger",
      "light",
      "dark",
    ];

    // eslint-disable-next-line no-plusplus
    for (let i = 0, len = colors.length; i < len; i++) {
      $themeColors[colors[i]] = useCssVar(
        `--${colors[i]}`,
        document.documentElement
      ).value.trim();
    }

    // Set Theme Breakpoints
    const breakpoints = ["xs", "sm", "md", "lg", "xl"];

    // eslint-disable-next-line no-plusplus
    for (let i = 0, len = breakpoints.length; i < len; i++) {
      $themeBreakpoints[breakpoints[i]] = Number(
        useCssVar(
          `--breakpoint-${breakpoints[i]}`,
          document.documentElement
        ).value.slice(0, -2)
      );
    }

    // Set RTL
    const { isRTL } = $themeConfig.layout;
    document.documentElement.setAttribute("dir", isRTL ? "rtl" : "ltr");
  },
  setup() {
    const { skin, skinClasses } = useAppConfig();
    const { enableScrollToTop } = $themeConfig.layout;

    // If skin is dark when initialized => Add class to body
    if (skin.value === "dark") document.body.classList.add("dark-layout");

    // Provide toast for Composition API usage
    // This for those apps/components which uses composition API
    // Demos will still use Options API for ease
    provideToast({
      hideProgressBar: true,
      closeOnClick: false,
      closeButton: false,
      icon: false,
      timeout: 3000,
      transition: "Vue-Toastification__fade",
    });

    // Set Window Width in store

    store.commit("app/UPDATE_WINDOW_WIDTH", window.innerWidth);
    const { width: windowWidth } = useWindowSize();
    watch(windowWidth, (val) => {
      store.commit("app/UPDATE_WINDOW_WIDTH", val);
    });

    return {
      skinClasses,
      enableScrollToTop,
    };
  },
};
</script>
